import React from "react";

import { faClock } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";
import { el } from "redom";

import ViewportContext, { Viewport } from "~/common/ViewportContext";
import { DailyUpdater } from "~/lib/dailyUpdate";
import * as dom from "~/lib/dom";

import styles from "./css/DueDate.module.css";

type DateProps = React.HTMLAttributes<HTMLElement> & {
	as: any; // eslint-disable-line @typescript-eslint/no-explicit-any
	date: string;
	done: boolean;
	long: boolean;
	short: boolean;
	styled: boolean;
	withClock: boolean;
};

export default class DateComponent extends React.Component<DateProps> {
	static contextType = ViewportContext;
	declare context: React.ContextType<typeof ViewportContext>;

	static defaultProps = {
		as: "div",
		date: null,
		done: false,
		long: false,
		short: false,
		styled: true,
		withClock: false,
	};

	state = {
		now: new Date(),
	};

	timeout: null | number = null;
	autoUpdate = () => {
		if (this.timeout) {
			clearTimeout(this.timeout);
		}
		const tomorrow = moment().endOf("day");
		this.timeout = window.setTimeout(
			() => {
				const now = new Date();
				if (now.getDate() != this.state.now.getDate()) {
					this.setState({ now });
				}
			},
			tomorrow.diff(Date.now(), "millisecond"),
		);
	};

	componentDidMount() {
		this.autoUpdate();
	}

	componentDidUpdate() {
		this.autoUpdate();
	}

	componentWillUnmount() {
		if (this.timeout) {
			clearTimeout(this.timeout);
		}
	}

	render() {
		const { as: Wrapper, date, done, long, short, styled, withClock, ...extraProps } = this.props;
		const { now } = this.state;
		const givenDate = moment(date, "YYYY-MM-DD");

		let shortFormat, yearFormat;

		if (long) {
			shortFormat = "ddd, MMM D, YYYY";
			yearFormat = "MMM D, YYYY";
		} else if (this.context.isMobile || short) {
			shortFormat = "MMM D";
			yearFormat = "MM/DD/YYYY";
		} else {
			shortFormat = "ddd, MMM D";
			yearFormat = "MMM D, YYYY";
		}

		let dateClasses = styled ? [styles.dueDate] : [];
		let displayedDate = givenDate.format(shortFormat);

		if (!long && givenDate.isSame(now, "day")) {
			displayedDate = "Today";
		} else if (!long && givenDate.isSame(moment().subtract(1, "days"), "day")) {
			displayedDate = "Yesterday";
		} else if (!long && givenDate.isSame(moment().add(1, "days"), "day")) {
			displayedDate = "Tomorrow";
		} else if (givenDate.diff(now, "months") >= 6) {
			displayedDate = givenDate.format(yearFormat);
		}

		if (!done && styled) {
			if (givenDate.isBefore(now, "day")) {
				dateClasses.push(styles.overdue);
			} else if (givenDate.diff(now, "days") < 7) {
				dateClasses.push(styles.dueSoon);
			}
		}

		return (
			<Wrapper {...extraProps} className={cx(dateClasses, extraProps.className)}>
				{withClock && <FontAwesomeIcon icon={faClock} />} {displayedDate}
			</Wrapper>
		);
	}
}

export interface DueDateData {
	date: moment.Moment;
	done: boolean;
	class: string;
}

// Implements a subset (needed by notes editor) of the above React component
// using the DOM API.
export class DueDateDOMComponent implements dom.DOMComponent<DueDateData> {
	el: HTMLElement;
	private data?: DueDateData;
	private updater: DailyUpdater;

	static template = el("span", { class: styles.dueDate });

	constructor(data: DueDateData) {
		this.el = dom.cloneNode(DueDateDOMComponent.template);
		this.updater = new DailyUpdater(this.doUpdate);
		this.updater.start();
		window.addEventListener("resize", this.doUpdate);
		this.update(data);
	}

	deinit() {
		this.updater.stop();
		window.removeEventListener("resize", this.doUpdate);
	}

	update(data: DueDateData) {
		const { date, done } = data;

		let shortFormat, yearFormat;
		if (Viewport.isMobile()) {
			shortFormat = "MMM D";
			yearFormat = "MM/DD/YYYY";
		} else {
			shortFormat = "ddd, MMM D";
			yearFormat = "MMM D, YYYY";
		}

		let displayedDate = date.format(shortFormat);
		if (date.isSame(moment(), "day")) {
			displayedDate = "Today";
		} else if (date.isSame(moment().subtract(1, "day"), "day")) {
			displayedDate = "Yesterday";
		} else if (date.isSame(moment().add(1, "day"), "day")) {
			displayedDate = "Tomorrow";
		} else if (date.diff(moment(), "months") >= 6) {
			displayedDate = date.format(yearFormat);
		}

		if (displayedDate !== this.el.innerText) {
			this.el.innerText = displayedDate;
		}

		if (this.data && data.class !== this.data.class) this.el.classList.remove(...this.data.class.split(" "));
		this.el.classList.toggle(styles.overdue, !done && date.isBefore(moment(), "day"));
		this.el.classList.toggle(styles.dueSoon, !done && date.diff(moment(), "days") < 7);
		if (data.class !== this.data?.class) this.el.classList.add(...data.class.split(" "));

		this.data = data;
	}

	private doUpdate = () => {
		if (this.data) this.update(this.data);
	};
}
